import { apiClient } from './client'
import type { DashboardUserRole } from './auth'

export type GroupType = 'Public' | 'Private' | 'InviteOnly'

export type AdminGroup = {
  id: string
  name: string
  description?: string | null
  type: GroupType
  isLocationBased: boolean
  memberCount: number
  maxMembers?: number | null
  creatorId: string
  creatorLabel: string
  creatorUsername?: string | null
  creatorEmail?: string | null
  creatorDashboardRole?: DashboardUserRole | null
  source?: AdminGroupSource | null
  createdAt: string
  updatedAt: string
}

export type AdminGroupSource = {
  type: string
  sourceId: string
  sourceName?: string | null
}

export type AdminGroupListResponse = {
  groups: AdminGroup[]
  totalCount: number
}

export type AdminGroupOwnerOption = {
  userId: string
  label: string
  email: string
  role: Exclude<DashboardUserRole, 'Guest' | 'TourAdmin' | 'SupportAgent' | 'SupportAdmin'>
  hasMobileProfile: boolean
}

export type AdminGroupPayload = {
  name: string
  description?: string | null
  type: GroupType
  isLocationBased: boolean
  maxMembers?: number | null
}

export type AdminCreateGroupPayload = AdminGroupPayload & {
  ownerUserId?: string | null
}

export type AdminPopularHikeGroupSyncPayload = {
  ownerUserId?: string | null
}

export type AdminGroupSyncResult = {
  group: AdminGroup
  created: boolean
  sourceType: string
  sourceId: string
  sourceName?: string | null
}

export type AdminBulkGroupSyncResult = {
  results: AdminGroupSyncResult[]
  totalCount: number
  createdCount: number
  existingCount: number
}

export type AdminGroupPost = {
  id: string
  author: {
    userId: string
    username: string
    profileImageUrl?: string | null
  }
  postAsRole?: string | null
  isAdminAuthored: boolean
  isPinned: boolean
  pinnedAt?: string | null
  content?: string | null
  imageUrls: string[]
  likeCount: number
  commentCount: number
  shareCount: number
  createdAt: string
  updatedAt: string
}

export type AdminGroupPostListResponse = {
  posts: AdminGroupPost[]
  totalCount: number
}

export type AdminCreateGroupPostPayload = {
  content: string
}

export async function getAdminGroups(params?: { query?: string; type?: GroupType; limit?: number }) {
  const { data } = await apiClient.get<AdminGroupListResponse>('/api/admin/social/groups', { params })
  return data
}

export async function getAdminGroupOwnerOptions() {
  const { data } = await apiClient.get<AdminGroupOwnerOption[]>('/api/admin/social/group-owner-options')
  return data
}

export async function createAdminGroup(payload: AdminCreateGroupPayload) {
  const { data } = await apiClient.post<AdminGroup>('/api/admin/social/groups', payload)
  return data
}

export async function updateAdminGroup(groupId: string, payload: AdminGroupPayload) {
  const { data } = await apiClient.put<AdminGroup>(`/api/admin/social/groups/${groupId}`, payload)
  return data
}

export async function deleteAdminGroup(groupId: string) {
  await apiClient.delete(`/api/admin/social/groups/${groupId}`)
}

export async function getAdminGroupPosts(groupId: string, params?: { limit?: number }) {
  const { data } = await apiClient.get<AdminGroupPostListResponse>(
    `/api/admin/social/groups/${groupId}/posts`,
    { params }
  )
  return data
}

export async function createAdminGroupPost(groupId: string, payload: AdminCreateGroupPostPayload) {
  const { data } = await apiClient.post<AdminGroupPost>(
    `/api/admin/social/groups/${groupId}/posts`,
    payload
  )
  return data
}

export async function pinAdminGroupPost(groupId: string, postId: string) {
  const { data } = await apiClient.post<AdminGroupPost>(
    `/api/admin/social/groups/${groupId}/posts/${postId}/pin`,
    {}
  )
  return data
}

export async function unpinAdminGroupPost(groupId: string, postId: string) {
  const { data } = await apiClient.delete<AdminGroupPost>(
    `/api/admin/social/groups/${groupId}/posts/${postId}/pin`
  )
  return data
}

export async function syncPopularHikeGroup(
  popularHikeId: string,
  payload: AdminPopularHikeGroupSyncPayload = {}
) {
  const { data } = await apiClient.post<AdminGroupSyncResult>(
    `/api/admin/social/groups/popular-hikes/${popularHikeId}/sync`,
    payload
  )
  return data
}

export async function syncAllPopularHikeGroups(payload: AdminPopularHikeGroupSyncPayload = {}) {
  const { data } = await apiClient.post<AdminBulkGroupSyncResult>(
    '/api/admin/social/groups/popular-hikes/sync-all',
    payload
  )
  return data
}
